import type BaseEnumType from "@/app/services/baseEnumType";
import Key from "@/app/types/common/key";
import type { Maybe } from "@/components/utils/typeHelpers";
import { translate, translateWithParams } from "@/plugins/i18n";

class NounMessages {
  private _translationKey: string;
  constructor(translationKey: string) {
    this._translationKey = translationKey;
  }

  Singular() {
    return translate(Key.fromString(this._translationKey));
  }

  Plural() {
    return translate(Key.fromString(this._translationKey + "_plural"));
  }

  SingleOrPlural(count: number) {
    return count === 1 ? this.Singular() : this.Plural();
  }

  CopySingle() {
    return translateWithParams("noun_copy_noun", { noun: this.Singular() });
  }

  ViewSingle() {
    return translateWithParams("noun_view_noun", { noun: this.Singular() });
  }

  DeleteSingle() {
    return translateWithParams("noun_delete_noun", { noun: this.Singular() });
  }

  CreateSingle() {
    return translateWithParams("noun_create_noun", { noun: this.Singular() });
  }

  HideSingle() {
    return translateWithParams("noun_hide_noun", { noun: this.Singular() });
  }

  UnhideSingle() {
    return translateWithParams("noun_unhide_noun", { noun: this.Singular() });
  }

  EditSingle() {
    return translateWithParams("noun_edit_noun", { noun: this.Singular() });
  }

  OpenSingle() {
    return translateWithParams("noun_open_noun", { noun: this.Singular() });
  }

  AddSingle() {
    return translateWithParams("noun_add_noun", { noun: this.Singular() });
  }

  DuplicateSingle() {
    return translateWithParams("noun_duplicate_noun", {
      noun: this.Singular(),
    });
  }
}

export class EnumNoun implements BaseEnumType {
  private static allValues: EnumNoun[] = []; // make sure this is top-most

  public static batch = new EnumNoun(1, "Batch", "enum_noun_batch");
  public static recipe = new EnumNoun(2, "Recipe", "enum_noun_recipe");
  public static process = new EnumNoun(3, "Process", "enum_noun_process");
  public static outletSubType = new EnumNoun(
    4,
    "OutletSubType",
    "enum_noun_outlet_sub_type"
  );
  public static order = new EnumNoun(5, "Order", "enum_noun_order");
  public static outlet = new EnumNoun(6, "Outlet", "enum_noun_outlet");
  public static group = new EnumNoun(7, "Group", "enum_noun_group");
  public static dutyReturn = new EnumNoun(
    8,
    "DutyReturn",
    "enum_noun_duty_return"
  );
  public static credit = new EnumNoun(9, "Credit", "enum_noun_credit");
  public static pricingCategory = new EnumNoun(
    10,
    "PricingCategory",
    "enum_noun_pricing_category"
  );
  public static product = new EnumNoun(11, "Product", "enum_noun_product");
  public static discount = new EnumNoun(12, "Discount", "enum_noun_discount");
  public static pallet = new EnumNoun(13, "Pallet", "enum_noun_pallet");
  public static item = new EnumNoun(14, "Item", "enum_noun_item");
  public static ullage = new EnumNoun(15, "Ullage", "enum_noun_ullage");

  public static stockTransfer = new EnumNoun(
    16,
    "StockTransfer",
    "enum_noun_stock_transfer"
  );

  public static stockBatch = new EnumNoun(
    17,
    "StockBatch",
    "enum_noun_stock_batch"
  );

  public static contact = new EnumNoun(18, "Contact", "enum_noun_contact");
  public static purchaseOrder = new EnumNoun(
    19,
    "PurchaseOrder",
    "enum_noun_purchase_order"
  );
  public static plannedSalesVisit = new EnumNoun(
    20,
    "PlannedSalesVisit",
    "enum_noun_planned_sales_visit"
  );
  public static eprBom = new EnumNoun(21, "EprBom", "enum_noun_epr_bom");
  public static eprBomLine = new EnumNoun(
    22,
    "EprBomLine",
    "enum_noun_epr_bom_line"
  );
  public static eprReport = new EnumNoun(
    23,
    "EprReport",
    "enum_noun_epr_report"
  );
  public static salesQuestion = new EnumNoun(
    24,
    "SalesQuestion",
    "enum_noun_sales_question"
  );
  public static regionalTax = new EnumNoun(
    25,
    "RegionalTax",
    "enum_noun_regional_tax"
  );
  public static site = new EnumNoun(26, "Site", "enum_noun_site");
  public static vehicle = new EnumNoun(27, "Vehicle", "enum_noun_vehicle");
  public static deliveryArea = new EnumNoun(
    28,
    "DeliveryArea",
    "enum_noun_delivery_area"
  );

  public key: number;
  public value: string;
  public translationKey: string;

  private constructor(key: number, value: string, translationKey: string) {
    this.key = key;
    this.value = value;
    this.translationKey = translationKey;
    EnumNoun.allValues.push(this);
  }

  public equals(other: Maybe<EnumNoun>): boolean {
    return this.key === other?.key;
  }

  public isBatch() {
    return this.equals(EnumNoun.batch);
  }
  public isRecipe() {
    return this.equals(EnumNoun.recipe);
  }
  public isProcess() {
    return this.equals(EnumNoun.process);
  }
  public isOutletSubType() {
    return this.equals(EnumNoun.outletSubType);
  }
  public isOrder() {
    return this.equals(EnumNoun.order);
  }
  public isOutlet() {
    return this.equals(EnumNoun.outlet);
  }
  public isGroup() {
    return this.equals(EnumNoun.group);
  }
  public isDutyReturn() {
    return this.equals(EnumNoun.dutyReturn);
  }
  public isCredit() {
    return this.equals(EnumNoun.credit);
  }
  public isPricingCategory() {
    return this.equals(EnumNoun.pricingCategory);
  }
  public isProduct() {
    return this.equals(EnumNoun.product);
  }
  public isDiscount() {
    return this.equals(EnumNoun.discount);
  }
  public isPallet() {
    return this.equals(EnumNoun.pallet);
  }
  public isItem() {
    return this.equals(EnumNoun.item);
  }
  public isUllage() {
    return this.equals(EnumNoun.ullage);
  }
  public isStockTransfer() {
    return this.equals(EnumNoun.stockTransfer);
  }
  public isStockBatch() {
    return this.equals(EnumNoun.stockBatch);
  }
  public isContact() {
    return this.equals(EnumNoun.contact);
  }
  public isRegionalTax() {
    return this.equals(EnumNoun.regionalTax);
  }
  public isPurchaseOrder() {
    return this.equals(EnumNoun.purchaseOrder);
  }
  public isPlannedSalesVisit() {
    return this.equals(EnumNoun.plannedSalesVisit);
  }
  public isEprBom() {
    return this.equals(EnumNoun.eprBom);
  }
  public isEprBomLine() {
    return this.equals(EnumNoun.eprBomLine);
  }
  public isEprReport() {
    return this.equals(EnumNoun.eprReport);
  }
  public isSalesQuestion() {
    return this.equals(EnumNoun.salesQuestion);
  }

  public static getValues(): EnumNoun[] {
    return EnumNoun.allValues;
  }

  public static getByKey(key: number): EnumNoun {
    const found = EnumNoun.allValues.find((e) => e.key === key);
    if (found === undefined) {
      throw new Error(`EnumNoun - Key not found: ${key}`);
    }

    return found;
  }

  get messages(): NounMessages {
    return new NounMessages(this.translationKey);
  }
}
